import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ActionItems as ActionItemsApi, Users, DealContacts } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { ActionItem } from "@/api/types";
import { fmtDayShort } from "@/lib/format";
import ActionItemForm from "./ActionItemForm";
import { confirmDialog, toast } from "@/lib/feedback";

interface Props {
  dealId: string;
  parentActivityId: string;
  items: ActionItem[];
  canEdit: boolean;
  currentUserId: string;
  onMutated: () => void;
  onAdded: () => void;
}

export default function ActionItems({
  dealId,
  parentActivityId,
  items,
  canEdit,
  currentUserId,
  onMutated,
  onAdded,
}: Props) {
  const [adding, setAdding] = useState(false);

  const usersQuery = useQuery({ queryKey: ["users"], queryFn: () => Users.list() });
  const contactsQuery = useQuery({
    queryKey: ["deal-contacts", dealId],
    queryFn: () => DealContacts.list(dealId),
  });

  const userMap = Object.fromEntries((usersQuery.data ?? []).map((u) => [u.id, u.name]));
  const contactMap = Object.fromEntries(
    (contactsQuery.data ?? []).map((dc) => [dc.contact.id, dc.contact.name]),
  );

  const toggleMutation = useMutation({
    mutationFn: (item: ActionItem) =>
      ActionItemsApi.update(dealId, item.id, {
        status: item.status === "done" ? "open" : "done",
      }),
    onSuccess: onMutated,
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const reassignMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      ActionItemsApi.update(dealId, id, { assignee_user_id: userId || null }),
    onSuccess: onMutated,
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ActionItemsApi.remove(dealId, id),
    onSuccess: onMutated,
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const addMutation = useMutation({
    mutationFn: (payload: Parameters<typeof ActionItemsApi.create>[1]) =>
      ActionItemsApi.create(dealId, payload),
    onSuccess: () => {
      onAdded();
      onMutated();
      setAdding(false);
    },
    onError: (e) => toast.err(`Could not add action item: ${apiErrorMessage(e)}`),
  });

  const mutating =
    toggleMutation.isPending || reassignMutation.isPending || deleteMutation.isPending;

  // Within each direction group, sort by due_date ascending with nulls last.
  const byDue = (a: ActionItem, b: ActionItem) => {
    if (a.due_date === b.due_date) return 0;
    if (a.due_date === null) return 1;
    if (b.due_date === null) return -1;
    return a.due_date < b.due_date ? -1 : 1;
  };
  const groups: Array<[string, ActionItem[]]> = [
    ["Us", items.filter((i) => i.direction === "us_to_them").sort(byDue)],
    ["Them", items.filter((i) => i.direction === "them_to_us").sort(byDue)],
  ];
  const colCount = canEdit ? 7 : 6;

  const renderRow = (i: ActionItem) => {
    const canToggle = canEdit || i.assignee_user_id === currentUserId;
    const isDone = i.status === "done";

    let assigneeName = "Unassigned";
    if (i.direction === "us_to_them" && i.assignee_user_id) {
      assigneeName = userMap[i.assignee_user_id] ?? "Unknown";
    } else if (i.direction === "them_to_us" && i.assignee_contact_id) {
      assigneeName = contactMap[i.assignee_contact_id] ?? "Unknown";
    }

    const createdBy = i.created_by_user_id ? (userMap[i.created_by_user_id] ?? "Unknown") : "—";

    return (
      <tr key={i.id} className="border-t border-fg/[0.04] hover:bg-fg/[0.02]">
        <td className="py-1 pr-2 align-middle w-0">
          <input
            type="checkbox"
            checked={isDone}
            disabled={!canToggle || mutating}
            onChange={() => toggleMutation.mutate(i)}
            className="block"
          />
        </td>
        <td className={`py-1 pr-3 align-middle ${isDone ? "line-through text-mut-500" : ""}`}>
          {i.text}
        </td>
        <td className="py-1 pr-3 align-middle text-mut-300">
          {canEdit && i.direction === "us_to_them" ? (
            <select
              className="input w-40 py-0 text-sm"
              value={i.assignee_user_id ?? ""}
              disabled={mutating}
              onChange={(e) => reassignMutation.mutate({ id: i.id, userId: e.target.value })}
              aria-label="Reassign to teammate"
            >
              <option value="">Unassigned</option>
              {(usersQuery.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          ) : (
            assigneeName
          )}
        </td>
        <td className="py-1 pr-3 align-middle text-mut-300 text-sm whitespace-nowrap">
          {i.due_date ? fmtDayShort(i.due_date) : "—"}
        </td>
        <td className="py-1 pr-3 align-middle text-mut-300 text-sm whitespace-nowrap">
          {createdBy}
        </td>
        <td className="py-1 pr-3 align-middle text-mut-300 text-sm whitespace-nowrap">
          {fmtDayShort(i.created_at)}
        </td>
        {canEdit && (
          <td className="py-1 align-middle w-0">
            <button
              type="button"
              className="text-mut-500 hover:text-accent-pink text-sm px-1"
              disabled={mutating}
              onClick={async () => {
                if (
                  await confirmDialog({
                    title: "Delete this action item?",
                    confirmLabel: "Delete",
                    danger: true,
                  })
                ) {
                  deleteMutation.mutate(i.id);
                }
              }}
              aria-label="Delete action item"
            >
              ✕
            </button>
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="mt-2 border-t border-fg/[0.06] pt-2">
      <div className="text-xs uppercase tracking-wide text-mut-500 mb-2">Action items</div>
      {items.length === 0 && !adding ? (
        <div className="text-sm text-mut-500 italic">None yet.</div>
      ) : items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-mut-500">
                <th className="font-normal py-1 pr-2 w-0" />
                <th className="font-normal py-1 pr-3">Item</th>
                <th className="font-normal py-1 pr-3">Assigned to</th>
                <th className="font-normal py-1 pr-3">Due</th>
                <th className="font-normal py-1 pr-3">Created by</th>
                <th className="font-normal py-1 pr-3">Created at</th>
                {canEdit && <th className="py-1 w-0" />}
              </tr>
            </thead>
            <tbody>
              {groups.flatMap(([label, group]) =>
                group.length === 0
                  ? []
                  : [
                      <tr key={`h-${label}`}>
                        <td
                          colSpan={colCount}
                          className="pt-2 pb-0.5 text-2xs font-semibold uppercase tracking-wider text-mut-500"
                        >
                          {label}
                        </td>
                      </tr>,
                      ...group.map(renderRow),
                    ],
              )}
            </tbody>
          </table>
        </div>
      ) : null}
      {canEdit &&
        (adding ? (
          <div className="mt-2">
            <ActionItemForm
              dealId={dealId}
              parentActivityId={parentActivityId}
              submitting={addMutation.isPending}
              onSubmit={(p) => addMutation.mutate(p)}
              onCancel={() => setAdding(false)}
            />
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-ghost text-sm mt-1"
            onClick={() => setAdding(true)}
          >
            + Add action item
          </button>
        ))}
    </div>
  );
}
